import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ProfileTab from './ProfileTab';
import ReportButton from './ReportButton';

type TabType = 'profile' | 'event';

interface StreamerInfoTabsProps {
  username: string;
  scheduleId?: string | null;
  isLoggedIn?: boolean;
  canReport?: boolean;
  // Event notification props (centralized state from parent)
  isNotificationSubscribed?: boolean;
  isNotificationSent?: boolean;
  notificationLoading?: boolean;
  notificationMessage?: { type: 'success' | 'error'; text: string } | null;
  onToggleNotification?: () => void;
}

export default function StreamerInfoTabs({
  username,
  scheduleId,
  isLoggedIn = false,
  canReport = false,
  isNotificationSubscribed = false,
  isNotificationSent = false,
  notificationLoading = false,
  notificationMessage,
  onToggleNotification,
}: StreamerInfoTabsProps) {
  const { t, i18n } = useTranslation('watch');
  // Force re-render when language changes
  const currentLanguage = i18n.language;
  const [activeTab, setActiveTab] = useState<TabType>('profile');

  const tabs: Array<{ key: TabType; label: string }> = [
    { key: 'profile', label: t('streamer.profile') },
    { key: 'event', label: t('streamer.eventNotification') },
  ];

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
      {/* Tab Navigation */}
      <div className="border-b-2 border-gray-200">
        <div className="flex gap-2">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-3 font-semibold transition-all border-b-2 -mb-0.5 ${
                activeTab === tab.key
                  ? 'text-pink-500 border-pink-500 bg-white'
                  : 'text-gray-600 border-transparent hover:text-gray-800'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="p-6">
        {activeTab === 'profile' && <ProfileTab key={currentLanguage} username={username} />}

        {activeTab === 'event' && (
          <div className="py-6">
            {/* Title */}
            <h3 className="text-lg font-bold text-pink-500 mb-4 flex items-center gap-2">
              <svg width="20" height="20" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
              </svg>
              {t('streamer.eventNotification')}
            </h3>

            {/* Description */}
            <div className="bg-gray-50 border-l-[3px] border-pink-500 p-4 mb-8 text-gray-500 text-sm leading-relaxed">
              {t('streamer.eventNotificationDesc')}
            </div>

            {scheduleId ? (
              <>
                {/* Status Box */}
                <div className="flex justify-start mb-8">
                  <div className="bg-gray-50 border border-gray-200 p-6 w-1/2">
                    <div className="flex flex-col gap-3">
                      <div className="flex items-center justify-between text-base">
                        <span className="text-gray-900 font-semibold">{t('streamer.registrationStatus')}</span>
                        <span className={isNotificationSubscribed ? 'text-green-600 font-semibold' : 'text-gray-500'}>
                          {isNotificationSubscribed ? t('streamer.registered') : t('streamer.notRegistered')}
                        </span>
                      </div>
                      {isNotificationSubscribed && (
                        <div className="flex items-center justify-between text-base border-t border-gray-200 pt-3">
                          <span className="text-gray-900 font-semibold">{t('streamer.notificationStatus')}</span>
                          <span className={isNotificationSent ? 'text-blue-600 font-semibold' : 'text-gray-500'}>
                            {isNotificationSent ? t('streamer.notificationSent') : t('streamer.notificationPending')}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Action Container */}
                <div className="flex justify-end">
                  <div className="flex flex-col items-center gap-4 bg-gray-100 p-6">
                    {isLoggedIn ? (
                      <>
                        <button
                          onClick={onToggleNotification}
                          disabled={notificationLoading}
                          className={`font-semibold py-4 px-12 transition-all hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed ${
                            isNotificationSubscribed
                              ? 'bg-gray-500 hover:bg-gray-600 text-white'
                              : 'bg-amber-500 hover:bg-amber-600 text-white'
                          }`}
                        >
                          {notificationLoading
                            ? t('beforeEntry.processing', '処理中...')
                            : isNotificationSubscribed
                              ? t('streamer.unregisterNotification', '通知を解除する')
                              : t('streamer.registerNotification')}
                        </button>
                        {notificationMessage && (
                          <p className={`text-sm ${notificationMessage.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                            {notificationMessage.text}
                          </p>
                        )}
                      </>
                    ) : (
                      <p className="text-gray-500 text-sm">
                        {t('streamer.loginRequiredForNotification')}
                      </p>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div className="flex justify-center">
                <div className="bg-gray-50 border border-gray-200 p-6 text-center">
                  <p className="text-gray-500">{t('streamer.noScheduledEvent')}</p>
                </div>
              </div>
            )}
          </div>
        )}
        <div className="mt-6 flex justify-end border-t border-gray-200 pt-4">
          <ReportButton subjectType="profile" streamerId={username} enabled={canReport} />
        </div>
      </div>
    </div>
  );
}
